'use client'

import { memo } from 'react'
import { useTranslations } from '@repo/core/i18n'
import { EmojiMehRegular, EmojiRegular, EmojiSadRegular, ErrorCircleRegular, QuestionCircleRegular } from '@fluentui/react-icons'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui'
import { cn } from '@repo/ui'
import type { Sentiment, SentimentLabel } from '../../entities/analysis'
import { getSentimentColor, getSentimentBgColor } from '../../entities/analysis'

interface SentimentBadgeProps {
  sentiment: Sentiment
  showScore?: boolean
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'outline' | 'minimal'
  className?: string
}

const sizeConfig = {
  sm: { icon: 'h-3 w-3', text: 'text-xs', padding: 'px-1.5 py-0.5', gap: 'gap-1' },
  md: { icon: 'h-4 w-4', text: 'text-sm', padding: 'px-2 py-1', gap: 'gap-1.5' },
  lg: { icon: 'h-5 w-5', text: 'text-base', padding: 'px-3 py-1.5', gap: 'gap-2' },
}

const SentimentIcon: Record<SentimentLabel, typeof EmojiRegular> = {
  positive: EmojiRegular,
  neutral: EmojiMehRegular,
  negative: EmojiSadRegular,
  mixed: ErrorCircleRegular,
}

export const SentimentBadge = memo(function SentimentBadge({
  sentiment,
  showScore = false,
  size = 'md',
  variant = 'default',
  className,
}: SentimentBadgeProps) {
  const t = useTranslations('aiAnalytics.sentimentBadge')
  const Icon = SentimentIcon[sentiment.label]
  const config = sizeConfig[size]

  const getVariantStyles = () => {
    switch (variant) {
      case 'outline':
        return cn(
          'border bg-transparent',
          sentiment.label === 'positive' && 'border-success text-success-foreground',
          sentiment.label === 'neutral' && 'border-gray-300 text-foreground',
          sentiment.label === 'negative' && 'border-destructive text-destructive-foreground',
          sentiment.label === 'mixed' && 'border-yellow-300 text-warning-foreground'
        )
      case 'minimal':
        return getSentimentColor(sentiment.label)
      default:
        return cn(getSentimentBgColor(sentiment.label), getSentimentColor(sentiment.label))
    }
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'inline-flex items-center rounded-full font-medium',
              config.padding,
              config.gap,
              getVariantStyles(),
              className
            )}
          >
            <Icon className={config.icon} />
            <span className={cn('capitalize', config.text)}>{sentiment.label}</span>
            {showScore && (
              <span className={cn('opacity-75', config.text)}>
                {t('confidence', { value: Math.round(sentiment.confidence * 100) })}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <SentimentTooltipContent sentiment={sentiment} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})

interface SentimentTooltipContentProps {
  sentiment: Sentiment
}

function SentimentTooltipContent({ sentiment }: SentimentTooltipContentProps) {
  const t = useTranslations('aiAnalytics.sentimentBadge')
  return (
    <div className="space-y-2 max-w-xs">
      <div className="flex items-center justify-between">
        <span className="font-medium capitalize">{sentiment.label} Sentiment</span>
        <span className="text-xs text-muted-foreground">
          {Math.round(sentiment.confidence * 100)}% confidence
        </span>
      </div>

      {sentiment.aspects.length > 0 && (
        <div className="pt-2 border-t space-y-1">
          <p className="text-xs font-medium">{t('detectedAspects')}</p>
          {sentiment.aspects.map((aspect, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span
                className={cn(
                  'w-2 h-2 rounded-full',
                  aspect.sentiment === 'positive' && 'bg-success',
                  aspect.sentiment === 'neutral' && 'bg-muted',
                  aspect.sentiment === 'negative' && 'bg-destructive'
                )}
              />
              <span className="capitalize">{aspect.aspect}</span>
              <span className="text-muted-foreground">- {aspect.sentiment}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Simplified icon-only version
interface SentimentIconOnlyProps {
  label: SentimentLabel
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const SentimentIconOnly = memo(function SentimentIconOnly({
  label,
  size = 'md',
  className,
}: SentimentIconOnlyProps) {
  const Icon = SentimentIcon[label]
  const config = sizeConfig[size]

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'inline-flex items-center justify-center rounded-full p-1',
              getSentimentBgColor(label),
              className
            )}
          >
            <Icon className={cn(config.icon, getSentimentColor(label))} />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <span className="capitalize">{label} sentiment</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})

// Unknown/pending state
interface SentimentPendingProps {
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const SentimentPending = memo(function SentimentPending({
  size = 'md',
  className,
}: SentimentPendingProps) {
  const t = useTranslations('aiAnalytics.sentimentBadge')
  const config = sizeConfig[size]

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-full bg-muted text-muted-foreground',
        config.padding,
        config.gap,
        className
      )}
    >
      <QuestionCircleRegular className={config.icon} />
      <span className={config.text}>{t('unknown')}</span>
    </div>
  )
})
